import React, { useEffect, useMemo, useRef } from 'react';
import { useGoogleMaps } from '../../../../hooks/useGoogleMaps.ts';
import type { CommerceDepartmentCity } from '../../../../services/commerceService.ts';
import {
  listedZoneJurisdictionLabel,
  parseListedZoneJurisdictionId,
} from '../../../../utils/listedZoneJurisdiction.ts';
import deliveryMapStyles from './CommerceDeliveryMap.module.scss';

const ARG_CENTER = { lat: -38.4161, lng: -63.6167 };

const FIT_PADDING = { top: 52, right: 56, bottom: 96, left: 52 };
const LISTED_ZONES_MAX_INITIAL_ZOOM = 7;

function geocodeToLatLng(g: any, address: string): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    try {
      const geo = new g.Geocoder();
      geo.geocode({ address }, (results: any, status: string) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const p = results[0].geometry.location;
          resolve({ lat: p.lat(), lng: p.lng() });
        } else resolve(null);
      });
    } catch {
      resolve(null);
    }
  });
}

interface CommerceListedZonesMapProps {
  jurisdictionName: string;
  cities: CommerceDepartmentCity[];
  selectedIds: string[];
  selectedLabelsJson: string;
}

const CommerceListedZonesMap: React.FC<CommerceListedZonesMapProps> = ({
  jurisdictionName,
  cities,
  selectedIds,
  selectedLabelsJson,
}) => {
  const selectedLabels = useMemo((): Record<string, string> => {
    try {
      return JSON.parse(selectedLabelsJson) as Record<string, string>;
    } catch {
      return {};
    }
  }, [selectedLabelsJson]);
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const cacheRef = useRef<Map<string, { lat: number; lng: number }>>(new Map());
  const { isLoaded, loadError } = useGoogleMaps();

  useEffect(() => {
    if (!isLoaded || loadError || !elRef.current) return;
    const g = (window as unknown as { google?: { maps: any } }).google?.maps;
    if (!g) return;

    const map = new g.Map(elRef.current, {
      center: ARG_CENTER,
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
      scrollwheel: true,
      gestureHandling: 'greedy',
      draggable: true,
      clickableIcons: false,
      disableDefaultUI: false,
    });
    mapRef.current = map;
    console.log('[CommerceListedZonesMap] map initialized', {
      jurisdictionName,
      initialCenter: ARG_CENTER,
      initialZoom: 4,
    });

    const nudge = () => {
      try {
        g.event.trigger(map, 'resize');
      } catch {
        /* ignore */
      }
    };
    const ro = new ResizeObserver(() => {
      nudge();
    });
    ro.observe(elRef.current);
    requestAnimationFrame(nudge);
    const t = window.setTimeout(nudge, 280);

    return () => {
      console.log('[CommerceListedZonesMap] map cleanup');
      window.clearTimeout(t);
      ro.disconnect();
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      mapRef.current = null;
    };
  }, [isLoaded, loadError]);

  useEffect(() => {
    const map = mapRef.current;
    const g = (window as unknown as { google?: { maps: any } }).google?.maps;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    console.log('[CommerceListedZonesMap] render cycle', {
      jurisdictionName,
      selectedCount: selectedIds.length,
      selectedIds,
      citiesCount: cities.length,
    });
    if (!map || !g) return;
    if (!jurisdictionName.trim()) {
      map.setCenter(ARG_CENTER);
      map.setZoom(4);
      console.log('[CommerceListedZonesMap] empty jurisdiction, reset to ARG center');
      return;
    }

    let cancelled = false;
    console.log('[CommerceListedZonesMap] jurisdiction changed, preserving cache', {
      to: jurisdictionName,
      cacheSize: cacheRef.current.size,
    });

    const run = async () => {
      if (selectedIds.length === 0) {
        const prov = await geocodeToLatLng(g, `${jurisdictionName}, Argentina`);
        if (cancelled) return;
        if (prov) {
          map.setCenter(prov);
          map.setZoom(6);
          console.log('[CommerceListedZonesMap] no selected zones, centered province', {
            jurisdictionName,
            center: prov,
            zoom: 6,
          });
        } else {
          map.setCenter(ARG_CENTER);
          map.setZoom(4);
          console.log('[CommerceListedZonesMap] province geocode failed, fallback ARG center', {
            jurisdictionName,
          });
        }
        return;
      }

      const bounds = new g.LatLngBounds();
      let placed = 0;
      for (let i = 0; i < selectedIds.length; i += 1) {
        if (cancelled) return;
        const id = selectedIds[i];
        const inCatalog = cities.some((c) => c.id === id);
        const label = cities.find((c) => c.id === id)?.name ?? selectedLabels[id] ?? id;
        let loc = cacheRef.current.get(id);
        if (!loc) {
          await new Promise((r) => {
            window.setTimeout(r, 200 + i * 40);
          });
          if (cancelled) return;
          const zoneJurId = parseListedZoneJurisdictionId(id);
          const zoneJurisdictionName =
            zoneJurId != null ? listedZoneJurisdictionLabel(zoneJurId) : jurisdictionName;
          const qWithJurisdiction = `${label}, ${zoneJurisdictionName}, Argentina`;
          console.log('[CommerceListedZonesMap] geocoding zone', {
            id,
            label,
            query: qWithJurisdiction,
            zoneJurisdictionName,
            inCatalog,
          });
          loc = (await geocodeToLatLng(g, qWithJurisdiction)) ?? undefined;
          if (!loc) {
            const qFallback = `${label}, Argentina`;
            console.log('[CommerceListedZonesMap] geocoding fallback', {
              id,
              label,
              query: qFallback,
            });
            loc = (await geocodeToLatLng(g, qFallback)) ?? undefined;
          }
          if (loc) cacheRef.current.set(id, loc);
        }
        console.log('[CommerceListedZonesMap] zone position', {
          id,
          label,
          found: Boolean(loc),
          location: loc ?? null,
          fromCache: cacheRef.current.has(id),
        });
        if (!loc || cancelled) continue;
        bounds.extend(loc);
        const marker = new g.Marker({
          position: loc,
          map,
          title: label,
          zIndex: 10 + i,
        });
        markersRef.current.push(marker);
        placed += 1;
      }
      if (cancelled) return;
      if (placed > 0) {
        const capZoom = () => {
          const currentZoom = map.getZoom();
          if (typeof currentZoom === 'number' && currentZoom > LISTED_ZONES_MAX_INITIAL_ZOOM) {
            map.setZoom(LISTED_ZONES_MAX_INITIAL_ZOOM);
          }
        };
        try {
          map.fitBounds(bounds, FIT_PADDING);
          g.event.addListenerOnce(map, 'idle', capZoom);
          console.log('[CommerceListedZonesMap] fitBounds applied', {
            placed,
            selectedCount: selectedIds.length,
            fitPadding: FIT_PADDING,
          });
        } catch {
          map.setCenter(bounds.getCenter());
          map.setZoom(LISTED_ZONES_MAX_INITIAL_ZOOM);
          console.log('[CommerceListedZonesMap] fitBounds failed, fallback center/zoom', {
            placed,
            zoom: LISTED_ZONES_MAX_INITIAL_ZOOM,
          });
        }
        requestAnimationFrame(() => {
          try {
            g.event.trigger(map, 'resize');
            map.fitBounds(bounds, FIT_PADDING);
            g.event.addListenerOnce(map, 'idle', capZoom);
            console.log('[CommerceListedZonesMap] refit after resize');
          } catch {
            /* ignore */
          }
        });
      } else {
        console.log('[CommerceListedZonesMap] no markers placed from selected zones', {
          selectedCount: selectedIds.length,
        });
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, jurisdictionName, cities, selectedIds, selectedLabelsJson]);

  if (loadError) {
    return (
      <p className={deliveryMapStyles.mapFallback}>
        No se pudo cargar el mapa para las zonas. Podés seguir eligiendo localidades en la lista.
      </p>
    );
  }

  if (!isLoaded) {
    return <div className={deliveryMapStyles.mapLoading}>Cargando mapa…</div>;
  }

  return <div ref={elRef} className={deliveryMapStyles.mapEl} role="presentation" aria-label="Mapa de zonas elegidas" />;
};

export default CommerceListedZonesMap;
